import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiClock,
  FiPause,
  FiPlay,
  FiXCircle,
  FiEdit,
  FiUsers,
  FiTarget,
  FiTrendingUp,
  FiTrash2
} from 'react-icons/fi';
import {
  getTaskById,
  pauseTask,
  resumeTask,
  extendDeadline,
  endTaskEarly,
  getTaskStatistics,
  deleteTask,
  cancelTask
} from '../../../api/retailPerformanceTasks';
import { useAuth } from '../../../contexts/AuthContext';
import CountdownTimer from '../../../components/retail/performance-tasks/CountdownTimer';
import ProgressBar from '../../../components/retail/performance-tasks/ProgressBar';
import ReassignTaskModal from '../../../components/retail/performance-tasks/ReassignTaskModal';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const TaskDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [task, setTask] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [newEndTime, setNewEndTime] = useState('');
  const [endReason, setEndReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    fetchTaskDetails();
    const interval = setInterval(fetchTaskDetails, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [id]);

  const fetchTaskDetails = async () => {
    try {
      const [taskResponse, statsResponse] = await Promise.all([
        getTaskById(id),
        getTaskStatistics(id)
      ]);
      if (taskResponse.success) setTask(taskResponse.data);
      if (statsResponse.success) setStats(statsResponse.data);
    } catch (error) {
      toast.error('Failed to load task details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async () => {
    if (!window.confirm('Are you sure you want to pause this task?')) return;
    setActionLoading(true);
    try {
      const response = await pauseTask(id);
      if (response.success) {
        toast.success('Task paused successfully');
        await fetchTaskDetails();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to pause task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      const response = await resumeTask(id);
      if (response.success) {
        toast.success('Task resumed successfully');
        await fetchTaskDetails();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resume task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtend = async () => {
    if (!newEndTime) return toast.error('Please select new end time');
    setActionLoading(true);
    try {
      const response = await extendDeadline(id, newEndTime);
      if (response.success) {
        toast.success('Deadline extended successfully');
        setShowExtendModal(false);
        setNewEndTime('');
        await fetchTaskDetails();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to extend deadline');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnd = async () => {
    if (!endReason.trim()) return toast.error('Please provide a reason');
    setActionLoading(true);
    try {
      const response = await endTaskEarly(id, endReason);
      if (response.success) {
        toast.success('Task ended successfully');
        setShowEndModal(false);
        setEndReason('');
        await fetchTaskDetails();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to end task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) return toast.error('Please provide a reason');
    setActionLoading(true);
    try {
      const response = await cancelTask(id, cancelReason);
      if (response.success) {
        toast.success('Task cancelled successfully');
        setShowCancelModal(false);
        setCancelReason('');
        await fetchTaskDetails();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Are you sure you want to permanently delete this task? This action cannot be undone.'
      )
    )
      return;
    setActionLoading(true);
    try {
      const response = await deleteTask(id);
      if (response.success) {
        toast.success('Task deleted successfully');
        navigate('/performance-tasks/inactive');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete task');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  if (!task) return <div className="p-6 text-center">Task not found</div>;

  const getUnitLabel = () => {
    const units = {
      sales_count: 'sales',
      sales_amount: 'NGN',
      tiles_quantity: 'cartons',
      accessories_quantity: 'units'
    };
    return units[task.task_type] || '';
  };

  const canManage = isAdmin;
  const isInactive = ['completed', 'failed', 'cancelled', 'ended'].includes(task.status);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft /> Back
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{task.title}</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">{task.task_number}</p>
          </div>
          <span
            className={`px-4 py-2 rounded-full text-sm font-semibold ${
              task.status === 'active'
                ? 'bg-green-100 text-green-800'
                : task.status === 'paused'
                  ? 'bg-amber-100 text-amber-800'
                  : task.status === 'completed'
                    ? 'bg-emerald-100 text-emerald-800'
                    : task.status === 'failed'
                      ? 'bg-red-100 text-red-800'
                      : task.status === 'ended'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-blue-100 text-blue-800'
            }`}
          >
            {task.status.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Progress */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <FiTrendingUp /> Task Progress
            </h2>
            <ProgressBar
              current={parseFloat(task.current_progress) || 0}
              target={parseFloat(task.target_value)}
              targetMin={task.target_min ? parseFloat(task.target_min) : null}
              targetMax={task.target_max ? parseFloat(task.target_max) : null}
              unit={getUnitLabel()}
              size="lg"
              showPercentage={true}
              showValues={true}
            />
          </div>

          {/* Description */}
          {task.description && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Description</h2>
              <p className="text-gray-700 dark:text-gray-300">{task.description}</p>
            </div>
          )}

          {/* Assigned Users Performance */}
          {task.assigned_users && task.assigned_users.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <FiUsers /> Assigned Users ({task.assigned_users.length})
                </h2>
                {canManage && ['scheduled', 'active', 'paused'].includes(task.status) && (
                  <button
                    onClick={() => setShowReassignModal(true)}
                    className="text-sm text-accent-gold hover:text-accent-goldDark font-medium flex items-center gap-1"
                  >
                    <FiEdit /> Manage
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {task.assigned_users.map((user) => (
                  <div key={user.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{user.full_name}</span>
                      <span className="text-sm text-gray-600">
                        {parseFloat(user.individual_progress_percentage || 0).toFixed(1)}%
                      </span>
                    </div>
                    <ProgressBar
                      current={parseFloat(user.individual_progress) || 0}
                      target={parseFloat(task.target_value)}
                      unit={getUnitLabel()}
                      size="sm"
                      showPercentage={false}
                      showValues={true}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pause History */}
          {task.pause_history && task.pause_history.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Pause History</h2>
              <div className="space-y-3">
                {task.pause_history.map((pause, idx) => (
                  <div key={idx} className="border-2 border-amber-500 pl-4 py-2">
                    <p className="text-sm text-gray-600">
                      Paused by {pause.paused_by_name} on{' '}
                      {new Date(pause.paused_at).toLocaleString()}
                    </p>
                    {pause.resumed_at && (
                      <p className="text-sm text-green-600">
                        Resumed by {pause.resumed_by_name} on{' '}
                        {new Date(pause.resumed_at).toLocaleString()}
                      </p>
                    )}
                    {pause.reason && <p className="text-sm italic">Reason: {pause.reason}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Timer */}
          {(task.status === 'scheduled' ||
            task.status === 'active' ||
            task.status === 'paused') && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <CountdownTimer
                targetTime={task.status === 'scheduled' ? task.start_time : task.end_time}
                mode={task.status === 'scheduled' ? 'start' : 'end'}
                extendedTime={task.extended_end_time}
                isPaused={task.status === 'paused'}
              />
            </div>
          )}

          {/* Admin Actions */}
          {canManage && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-3">
              <h3 className="font-semibold mb-4">Admin Actions</h3>
              {task.status === 'scheduled' && (
                <>
                  <button
                    onClick={() => setShowExtendModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    <FiClock /> Extend Deadline
                  </button>
                  <button
                    onClick={() => setShowReassignModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
                  >
                    <FiUsers /> Reassign / Manage Users
                  </button>
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    <FiXCircle /> Cancel Task
                  </button>
                </>
              )}
              {task.status === 'active' && (
                <>
                  <button
                    onClick={handlePause}
                    disabled={actionLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"
                  >
                    <FiPause /> Pause Task
                  </button>
                  <button
                    onClick={() => setShowExtendModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    <FiClock /> Extend Deadline
                  </button>
                  <button
                    onClick={() => setShowReassignModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
                  >
                    <FiUsers /> Reassign / Manage Users
                  </button>
                  <button
                    onClick={() => setShowEndModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    <FiXCircle /> End Task Early
                  </button>
                </>
              )}
              {task.status === 'paused' && (
                <button
                  onClick={handleResume}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                >
                  <FiPlay /> Resume Task
                </button>
              )}
              {isInactive && (
                <button
                  onClick={handleDelete}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-700 border border-red-200 rounded hover:bg-red-200 disabled:opacity-50 transition-colors"
                >
                  <FiTrash2 /> Delete Task
                </button>
              )}
            </div>
          )}

          {/* Statistics */}
          {stats && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4">Statistics</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total Sales:</span>
                  <span className="font-semibold">{stats.total_sales || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Created:</span>
                  <span className="font-semibold">
                    {new Date(task.created_at).toLocaleDateString()}
                  </span>
                </div>
                {task.completion_time && (
                  <div className="flex justify-between">
                    <span>Completed:</span>
                    <span className="font-semibold">
                      {new Date(task.completion_time).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Extend Modal */}
      {showExtendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Extend Deadline</h3>
            <input
              type="datetime-local"
              value={newEndTime}
              onChange={(e) => setNewEndTime(e.target.value)}
              className="w-full px-4 py-2 border rounded mb-4 dark:bg-gray-700"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowExtendModal(false)}
                className="flex-1 px-4 py-2 border rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleExtend}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                Extend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* End Modal */}
      {showEndModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">End Task Early</h3>
            <textarea
              value={endReason}
              onChange={(e) => setEndReason(e.target.value)}
              placeholder="Reason for ending..."
              rows="4"
              className="w-full px-4 py-2 border rounded mb-4 dark:bg-gray-700"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndModal(false)}
                className="flex-1 px-4 py-2 border rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleEnd}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
              >
                End Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Cancel Task</h3>
            <p className="text-gray-600 mb-4 text-sm">
              This will cancel the task for all assigned users. This action cannot be undone.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation..."
              rows="4"
              className="w-full px-4 py-2 border rounded mb-4 dark:bg-gray-700"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 border rounded"
              >
                Back
              </button>
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
              >
                Cancel Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Modal */}
      {showReassignModal && (
        <ReassignTaskModal
          isOpen={showReassignModal}
          onClose={() => setShowReassignModal(false)}
          onSuccess={() => {
            setShowReassignModal(false);
            fetchTaskDetails();
          }}
          taskId={task.id}
          currentAssignedUsers={task.assigned_users}
        />
      )}
    </div>
  );
};

export default TaskDetailPage;
